import { z } from "zod";

/**
 * #462: how a contact ended, in the owner's three words (2026-09-26).
 *
 * - `contained` - the caller got to their destination: the flow says so.
 * - `escalated` - they went, or would have gone, to an agent: a queue, a
 *   transfer, an agent, or the flow saying so.
 * - `not_contained` - dropped without completing: a hang-up, an exhausted
 *   no-input or no-match, an error.
 *
 * Only the flow knows what "done" means, so the flow says so with the
 * contact attribute below, the way `starsach_alias` names a profile.
 */
export const ContactOutcomeSchema = z.enum(["contained", "not_contained", "escalated"]);
export type ContactOutcome = z.infer<typeof ContactOutcomeSchema>;

/** The detail beneath `escalated`, from the record alone: an agent took it, it left by transfer, or it queued and never reached one. */
export const EscalationDetailSchema = z.enum(["answered", "transferred_out", "abandoned"]);
export type EscalationDetail = z.infer<typeof EscalationDetailSchema>;

/** The contact attribute a flow sets at a destination (a Set contact attributes block). */
export const FLOW_OUTCOME_ATTRIBUTE = "starsach_outcome";

/** What a flow may declare: a fixed set, never free text (ADR-0015 section 3). */
export const FlowOutcomeSchema = z.enum(["contained", "escalated"]);
export type FlowOutcome = z.infer<typeof FlowOutcomeSchema>;

/** The attribute's value as a flow outcome, or nothing: case and surrounding space forgiven, anything else dropped. */
export function flowOutcomeOf(value: string | null | undefined): FlowOutcome | undefined {
  if (typeof value !== "string") return undefined;
  return FlowOutcomeSchema.safeParse(value.trim().toLowerCase()).data;
}
